import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to handle session timeout based on user inactivity
 * @param {Function} onTimeout - Callback function to execute when session times out
 * @param {number} timeoutMinutes - Number of minutes of inactivity before timeout (default: 30)
 * @param {boolean} enabled - Whether the session timeout is enabled (default: true)
 */
export function useSessionTimeout(onTimeout, timeoutMinutes = 30, enabled = true) {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Clear existing timeout
  const clearTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Reset the timeout timer
  const resetTimeout = useCallback(() => {
    if (!enabled) {
      clearTimeout();
      return;
    }

    clearTimeout();
    
    const timeoutMs = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds
    
    timeoutRef.current = window.setTimeout(() => {
      // Check if user was active recently (within last minute)
      // This prevents logout if user was active but events were throttled
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity >= timeoutMs) {
        onTimeout();
      } else {
        // Reset with remaining time
        resetTimeout();
      }
    }, timeoutMs);
    
    lastActivityRef.current = Date.now();
  }, [onTimeout, timeoutMinutes, enabled, clearTimeout]);

  // Handle user activity events
  const handleActivity = useCallback(() => {
    if (!enabled) return;
    
    // Throttle activity updates to avoid excessive timer resets
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Only reset if at least 1 second has passed since last activity
    // This prevents excessive timer resets on rapid events
    if (timeSinceLastActivity >= 1000) {
      resetTimeout();
    }
  }, [enabled, resetTimeout]);

  useEffect(() => {
    if (!enabled) {
      clearTimeout();
      return;
    }

    // Initial timeout setup
    resetTimeout();

    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Add event listeners with passive option for better performance
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility change (tab focus/blur)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // When tab becomes visible, check if we should reset timeout
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        const timeoutMs = timeoutMinutes * 60 * 1000;
        
        if (timeSinceLastActivity < timeoutMs) {
          // Still within timeout period, reset timer
          resetTimeout();
        } else {
          // Tab was hidden for too long, trigger timeout
          onTimeout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      clearTimeout();
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, handleActivity, resetTimeout, timeoutMinutes, onTimeout, clearTimeout]);

  // Expose manual reset function (useful for API calls or other activities)
  return {
    resetTimeout: handleActivity,
  };
}

